import React, { useEffect, useMemo } from 'react'
import { returnsClickEvent } from '@components/returns/helpers'
import Translations from '@helpers/translations'
import { objectOmit } from '@helpers/utils'
import { RETURN_REASON_CODES } from '@components/returns/consts'
import { Button } from '@components/ui/Button'
import { Values, ExchangesSizeOption } from '@components/returns/types'
import ButtonsWrapper from '../../../shared/ButtonsWrapper'
import DropdownSizeOptionsList, {
  getAvailableSizeOptionsList
} from '../../../shared/DropdownSizeOptionsList'
import FormWrapper from '../../../shared/FormWrapper'
import styles from '../../../shared/styles.module.scss'
import { ProductSizes } from '@api/returns/types'
import FormGrid from '@components/ui/formElements/form/shared/FormGrid'

export type SizesStepProps = {
  values: Values & { sizes: Values['sizes'] & { available?: boolean } }
  sizes: Partial<ProductSizes>
  setSelection: (
    args: Partial<{
      color: Values['color']
      sizes: Values['sizes'] & { available?: boolean }
    }>
  ) => void
  exchangesSizeOptions: ExchangesSizeOption[]
  onUpdate: (step: string) => void
  isLastStep: boolean
}

export function getSingleOptionSelections(
  exchangesSizeOptions: ExchangesSizeOption[],
  values: Values
) {
  return exchangesSizeOptions.reduce<Record<string, string>>(
    (selections, option) => {
      const availableOptions = getAvailableSizeOptionsList(
        option,
        values
      ).filter(answer => answer.available !== false)
      if (!values.sizes[option.id] && availableOptions.length === 1) {
        selections[option.id] = String(availableOptions[0].value)
      }
      return selections
    },
    {}
  )
}

export function SizesStep({
  values,
  sizes,
  exchangesSizeOptions,
  setSelection,
  onUpdate,
  isLastStep
}: SizesStepProps) {
  const singleOptionSelections = useMemo(
    () => getSingleOptionSelections(exchangesSizeOptions, values),
    [exchangesSizeOptions, values]
  )
  const displayedSizes = useMemo(
    () => ({ ...values.sizes, ...singleOptionSelections }),
    [singleOptionSelections, values.sizes]
  )

  useEffect(() => {
    if (Object.keys(singleOptionSelections).length > 0) {
      setSelection({ sizes: displayedSizes })
    }
  }, [displayedSizes, setSelection, singleOptionSelections])

  const isReasonColor = values.reason === RETURN_REASON_CODES.COLOR
  const hasChangedSizes =
    JSON.stringify(objectOmit(values.sizes, ['available'])) !==
    JSON.stringify(sizes)

  const titleText = isReasonColor
    ? 'Do you want to change size?'
    : 'Select your new size'

  const isSizesExchangeValid =
    displayedSizes.available &&
    exchangesSizeOptions.every(
      option => !!displayedSizes[option.id as keyof typeof displayedSizes]
    )

  const buttonText = (() => {
    if (!isLastStep) {
      return 'Next'
    }

    if (isReasonColor && !hasChangedSizes) {
      return 'Keep this size'
    }

    return Translations.localise('pages.returns.done')
  })()

  const handleUpdate = (step: string) => {
    returnsClickEvent({
      label: buttonText
    })
    if (step === 'prev') {
      setSelection({ sizes, color: undefined }) // reset to original product sizes
    }
    onUpdate(step)
  }
  return (
    <>
      <FormWrapper title={titleText}>
        <div className={styles.returnsForm__sizesContainer}>
          <FormGrid>
            {exchangesSizeOptions.map(listItem => (
              <DropdownSizeOptionsList
                key={listItem.id}
                sizeOptions={listItem}
                setSelection={setSelection}
                sizes={sizes}
                values={{ ...values, sizes: displayedSizes }}
              />
            ))}
          </FormGrid>
        </div>
      </FormWrapper>
      <ButtonsWrapper onUpdate={handleUpdate}>
        <Button
          disabled={!isSizesExchangeValid}
          classes={styles.returnsForm__button}
          variant="primary"
          fullWidth="always"
          onClick={() => handleUpdate(isLastStep ? 'done' : 'next')}
        >
          {buttonText}
        </Button>
      </ButtonsWrapper>
    </>
  )
}
